/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
    BarChart2, Users, BookOpen, ClipboardList, Activity,
    Search,  ChevronLeft, ChevronRight,
    TrendingUp, UserCheck, UserX, Trophy, Clock,
} from 'lucide-react';
import Page from '../../../components/Page';
import Modal from '../../../components/modal/Index';
import reportSvc from '../../../services/clientAdmin/reportServices';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(v) {
    if (!v) return '—';
    return String(v).replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}
function fmtDate(v) {
    if (!v) return '—';
    return new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STATUS_BADGE = {
    'Passed':      'bg-green-100 text-green-700',
    'Failed':      'bg-red-100 text-red-700',
    'Completed':   'bg-green-100 text-green-700',
    'In Progress': 'bg-blue-100 text-blue-700',
    'Not Started': 'bg-gray-100 text-gray-500',
    'Enrolled':    'bg-purple-100 text-purple-700',
};

function StatusBadge({ value }) {
    return (
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[value] || 'bg-gray-100 text-gray-500'}`}>
            {value || '—'}
        </span>
    );
}

function TypeBadge({ type }) {
    return (
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${type === 'assessment' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
            {type === 'assessment' ? 'Assessment' : 'Course'}
        </span>
    );
}

// ─── Pagination ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;

function paginate(rows, page) {
    const total = rows.length;
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const slice = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
    return { slice, total, pages };
}

function Pagination({ page, total, pages, onChange }) {
    const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
    const to = Math.min((page + 1) * PAGE_SIZE, total);
    return (
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 text-xs text-gray-500">
            <span>{total === 0 ? 'No results' : `${from}–${to} of ${total}`}</span>
            <div className="flex items-center gap-1">
                <button onClick={() => onChange(page - 1)} disabled={page === 0}
                    className="w-7 h-7 flex items-center justify-center rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                    <ChevronLeft size={13} />
                </button>
                <span className="px-2 font-medium text-gray-600">{page + 1} / {pages}</span>
                <button onClick={() => onChange(page + 1)} disabled={page >= pages - 1}
                    className="w-7 h-7 flex items-center justify-center rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                    <ChevronRight size={13} />
                </button>
            </div>
        </div>
    );
}

// ─── Filter Bar ───────────────────────────────────────────────────────────────

function FilterBar({ departments, filters, onChange, showDepartment = true, showType = true, showStatus = false, onExport }) {
    return (
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50/60">
            <div className="flex items-center gap-2 flex-wrap">
                {showDepartment && (
                <select
                    value={filters.departmentId || ''}
                    onChange={e => onChange({ ...filters, departmentId: e.target.value || null })}
                    className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:border-purple-400"
                >
                    <option value="">All Departments</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                )}
                {showType && (
                    <select
                        value={filters.type || ''}
                        onChange={e => onChange({ ...filters, type: e.target.value || null })}
                        className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:border-purple-400"
                    >
                        <option value="">All Types</option>
                        <option value="course">Course</option>
                        <option value="assessment">Assessment</option>
                    </select>
                )}
                {showStatus && (
                    <select
                        value={filters.status || ''}
                        onChange={e => onChange({ ...filters, status: e.target.value || null })}
                        className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:border-purple-400"
                    >
                         <option value="">All Status</option>
                        <option value="Passed">Passed</option>
                        <option value="Failed">Failed</option>
                        <option value="Not Started">Not Started</option>
                        <option value="Enrolled">Enrolled</option>
                    </select>
                )}
                <div className="relative">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        value={filters.search || ''}
                        onChange={e => onChange({ ...filters, search: e.target.value })}
                        placeholder="Search…"
                        className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-purple-400 w-44"
                    />
                </div>
            </div>
            {/* Export hidden for now — pending verification of the authenticated XLSX export. Future task. */}
            {false && onExport && (
                <button
                    onClick={onExport}
                    className="flex items-center gap-1.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
                >
                    {/* <DownloadIcon size={14} /> */}
                    Export XLSX
                </button>
            )}
        </div>
    );
}

// ─── Empty / Skeleton ─────────────────────────────────────────────────────────

function Empty({ icon: Icon, message }) {
    return (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Icon size={36} className="mb-3 opacity-30" />
            <p className="text-sm">{message}</p>
        </div>
    );
}

function Skeleton({ cols = 6 }) {
    return (
        <div className="divide-y divide-gray-100">
            {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="flex gap-4 px-5 py-4 animate-pulse">
                    {Array.from({ length: cols }).map((_, j) => (
                        <div key={j} className="h-3 bg-gray-100 rounded flex-1" />
                    ))}
                </div>
            ))}
        </div>
    );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, color = 'purple' }) {
    const colors = {
        purple: 'bg-purple-50 text-purple-600',
        green:  'bg-green-50 text-green-600',
        red:    'bg-red-50 text-red-600',
        blue:   'bg-blue-50 text-blue-600',
        gray:   'bg-gray-50 text-gray-500',
    };
    return (
        <div className="bg-white border border-gray-100 rounded-xl p-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${colors[color]}`}>
                <Icon size={16} />
            </div>
            <div>
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-lg font-bold text-gray-900">{value ?? '—'}</p>
            </div>
        </div>
    );
}

// ─── Course Modal Content ─────────────────────────────────────────────────────

function CourseModalContent({ item }) {
    const [users, setUsers]     = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage]       = useState(0);
    const [search, setSearch]   = useState('');

    useEffect(() => {
        if (!item) return;
        setLoading(true);
        setSearch('');
        setPage(0);
        reportSvc.getContentEnrolledUsers(item.itemId, 'course')
            .then(r => { if (r.ok) setUsers(r.data?.users || []); })
            .finally(() => setLoading(false));
    }, [item?.itemId]);

    const filtered = users.filter(u =>
        !search || u.learnerName?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
    );

    const completed  = filtered.filter(u => u.completionPercent >= 100).length;
    const notStarted = filtered.filter(u => !u.completionPercent || u.completionPercent === 0).length;
    const avgProgress = filtered.length
        ? Math.round(filtered.reduce((s, u) => s + (u.completionPercent || 0), 0) / filtered.length)
        : 0;

    const { slice, total, pages } = paginate(filtered, page);

    return (
        <div>
            {/* Stats */}
            <div className="grid grid-cols-2 gap-3 mb-4">
                <StatCard icon={Users}      label="Total Enrolled" value={filtered.length}   color="purple" />
                <StatCard icon={TrendingUp} label="Avg Progress"   value={`${avgProgress}%`} color="blue" />
                <StatCard icon={UserCheck}  label="Completed"      value={completed}          color="green" />
                <StatCard icon={UserX}      label="Not Started"    value={notStarted}         color="gray" />
            </div>

            {/* Search */}
            <div className="relative mb-3">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(0); }}
                    placeholder="Search learners…"
                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400"
                />
            </div>

            {/* Table */}
            {loading ? <Skeleton cols={4} /> : filtered.length === 0 ? (
                <Empty icon={Users} message="No enrolled learners found." />
            ) : (
                <>
                    <div className="overflow-x-auto rounded-lg border border-gray-100">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    {['Learner', 'Enrolled', 'Progress', 'Status'].map(h => (
                                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {slice.map((u, i) => (
                                    <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-gray-900 text-sm">{u.learnerName}</div>
                                            <div className="text-xs text-gray-400">{u.email}</div>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmtDate(u.enrolledDate)}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-20 h-1.5 bg-gray-100 rounded-full">
                                                    <div
                                                        className="h-full rounded-full bg-purple-500 transition-all"
                                                        style={{ width: `${Math.min(u.completionPercent || 0, 100)}%` }}
                                                    />
                                                </div>
                                                <span className="text-xs text-gray-600 font-medium">{u.completionPercent || 0}%</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <StatusBadge value={
                                                u.completionPercent >= 100 ? 'Completed' :
                                                u.completionPercent > 0    ? 'In Progress' : 'Not Started'
                                            } />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination page={page} total={total} pages={pages} onChange={setPage} />
                </>
            )}
        </div>
    );
}

// ─── Assessment Modal Content ─────────────────────────────────────────────────

function AssessmentModalContent({ item }) {
    const [users, setUsers]     = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage]       = useState(0);
    const [search, setSearch]   = useState('');

    useEffect(() => {
        if (!item) return;
        setLoading(true);
        setSearch('');
        setPage(0);
        reportSvc.getAssessmentEnrolledUsers(item.assessmentId)
            .then(r => { if (r.ok) setUsers(r.data?.users || []); })
            .finally(() => setLoading(false));
    }, [item?.assessmentId]);

    const filtered = users.filter(u =>
        !search || u.learnerName?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
    );

  const uniqueUsers = Object.values(
  filtered.reduce((acc, user) => {
    acc[user.userId] = user;
    return acc;
  }, {})
);

const completed = uniqueUsers.filter(
  u => u.status === "Completed"
).length;

const notStarted = uniqueUsers.filter(
  u => u.status === "Not Started"
).length;

const attempted = uniqueUsers.filter(
  u => Number(u.totalAttempts) > 0
);

const topScorer = attempted.length
  ? attempted.reduce((a, b) =>
      Number(a.bestScore) > Number(b.bestScore) ? a : b
    )
  : null;

const highScore = topScorer
  ? Number(topScorer.bestScore)
  : 0;

const avgScore = attempted.length
  ? Math.round(
      attempted.reduce(
        (sum, u) => sum + Number(u.bestScore || 0),
        0
      ) / attempted.length
    )
  : 0;

    const { slice, total, pages } = paginate(filtered, page);

    return (
        <div>
            {/* Stats */}
            <div className="grid grid-cols-2 gap-3 mb-4">
                <StatCard icon={Users}      label="Total Enrolled"  value={filtered.length}                          color="purple" />
                <StatCard icon={UserCheck}  label="Completed"       value={completed}                                color="green" />
                <StatCard icon={UserX}      label="Not Attempted"   value={notStarted}                               color="gray" />
                <StatCard icon={TrendingUp} label="Avg Best Score"  value={attempted.length ? `${avgScore}%` : '—'} color="blue" />
            </div>

            {/* Top scorer banner */}
            {topScorer?.learnerName && (
                <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-4">
                    <Trophy size={16} className="text-amber-500 flex-shrink-0" />
                    <div>
                        <p className="text-xs text-amber-700 font-semibold">Top Scorer</p>
                        <p className="text-sm text-gray-900 font-medium">
                            {topScorer.learnerName}
                            <span className="ml-2 text-amber-600 font-bold">{highScore}%</span>
                        </p>
                    </div>
                </div>
            )}

            {/* Search */}
            <div className="relative mb-3">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(0); }}
                    placeholder="Search learners…"
                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400"
                />
            </div>

            {/* Table */}
            {loading ? <Skeleton cols={5} /> : filtered.length === 0 ? (
                <Empty icon={Users} message="No enrolled learners found." />
            ) : (
                <>
                    <div className="overflow-x-auto rounded-lg border border-gray-100">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    {['Learner', 'Enrolled', 'Attempts', 'Best Score', 'Status'].map(h => (
                                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {slice.map((u, i) => (
                                    <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-gray-900 text-sm">{u.learnerName}</div>
                                            <div className="text-xs text-gray-400">{u.email}</div>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmtDate(u.enrolledDate)}</td>
                                        <td className="px-4 py-3 text-gray-600 text-center">{u.totalAttempts || 0}</td>
                                        <td className="px-4 py-3 text-gray-700 font-medium text-center">
                                            {u.totalAttempts > 0 ? `${u.bestScore}%` : '—'}
                                        </td>
                                        <td className="px-4 py-3"><StatusBadge value={u.status} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination page={page} total={total} pages={pages} onChange={setPage} />
                </>
            )}
        </div>
    );
}

// ─── Tab: Courses ─────────────────────────────────────────────────────────────

function CourseContentTab() {
    const [rows, setRows]       = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch]   = useState('');
    const [page, setPage]       = useState(0);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        setLoading(true);
        reportSvc.getContentList()
            .then(r => { if (r.ok) setRows(r.data?.content?.filter(c => c.type === 'course') || []); })
            .finally(() => setLoading(false));
    }, []);

    const filtered = rows.filter(r => !search || r.title?.toLowerCase().includes(search.toLowerCase()));
    const { slice, total, pages } = paginate(filtered, page);

    return (
        <>
            <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50/60">
                <div className="relative">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                        placeholder="Search courses…"
                        className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-purple-400 w-56"
                    />
                </div>
            </div>

            {loading ? <Skeleton cols={3} /> : filtered.length === 0 ? (
                <Empty icon={BookOpen} message="No courses found." />
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    {['Course Title', 'Format', ''].map(h => (
                                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {slice.map((r, i) => (
                                    <tr key={i} className="hover:bg-purple-50/40 cursor-pointer transition-colors" onClick={() => setSelected(r)}>
                                        <td className="px-4 py-3 font-medium text-gray-900">{r.title}</td>
                                        <td className="px-4 py-3 text-gray-500 text-xs">{fmt(r.contentFormat) || '—'}</td>
                                        <td className="px-4 py-3 text-right">
                                            <span className="text-xs text-purple-600 font-medium">View Report →</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination page={page} total={total} pages={pages} onChange={setPage} />
                </>
            )}

            <Modal
                title={selected?.title || 'Course Report'}
                open={!!selected}
                toggle={() => setSelected(null)}
            >
                {selected && <CourseModalContent item={selected} />}
            </Modal>
        </>
    );
}

// ─── Tab: Assessments ─────────────────────────────────────────────────────────

function AssessmentContentTab() {
    const [rows, setRows]       = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch]   = useState('');
    const [page, setPage]       = useState(0);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        setLoading(true);
        reportSvc.getAssessmentList()
            .then(r => { if (r.ok) setRows(r.data?.assessments || []); })
            .finally(() => setLoading(false));
    }, []);

    const filtered = rows.filter(r => !search || r.title?.toLowerCase().includes(search.toLowerCase()));
    const { slice, total, pages } = paginate(filtered, page);

    return (
        <>
            <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50/60">
                <div className="relative">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                        placeholder="Search assessments…"
                        className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-purple-400 w-56"
                    />
                </div>
            </div>

            {loading ? <Skeleton cols={4} /> : filtered.length === 0 ? (
                <Empty icon={ClipboardList} message="No assessments found." />
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    {['Assessment Title', 'Type', 'Duration', ''].map(h => (
                                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {slice.map((r, i) => (
                                    <tr key={i} className="hover:bg-purple-50/40 cursor-pointer transition-colors" onClick={() => setSelected(r)}>
                                        <td className="px-4 py-3 font-medium text-gray-900">{r.title}</td>
                                        <td className="px-4 py-3 text-xs text-gray-500">{fmt(r.assessmentType) || '—'}</td>
                                        <td className="px-4 py-3 text-xs text-gray-500">
                                            {r.durationMinutes ? (
                                                <span className="flex items-center gap-1"><Clock size={11} />{r.durationMinutes} min</span>
                                            ) : '—'}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <span className="text-xs text-purple-600 font-medium">View Report →</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination page={page} total={total} pages={pages} onChange={setPage} />
                </>
            )}

            <Modal
                title={selected?.title || 'Assessment Report'}
                open={!!selected}
                toggle={() => setSelected(null)}
            >
                {selected && <AssessmentModalContent item={selected} />}
            </Modal>
        </>
    );
}

// ─── Existing Tabs ────────────────────────────────────────────────────────────

function LearnerPerformanceTab({ departments, filters, onFiltersChange, onExport }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);

    useEffect(() => {
        setLoading(true); setPage(0);
        reportSvc.getLearnerPerformance(filters)
            .then(r => { if (r.ok) setRows(r.data?.rows || []); })
            .finally(() => setLoading(false));
    }, [filters.departmentId, filters.type, filters.status]); 

    const filteredRows = rows.filter(r => {
        if (!filters.search?.trim()) return true;
        const q = filters.search.trim().toLowerCase();
        return r.learnerName?.toLowerCase().split(/\s+/).some(word => word.startsWith(q));
    });

    const { slice, total, pages } = paginate(filteredRows, page);

    return (
        <>
            <FilterBar departments={departments} filters={filters} onChange={onFiltersChange} showType showStatus onExport={onExport} />
            {loading ? <Skeleton cols={9} /> : filteredRows.length === 0 ? (
                <Empty icon={Users} message="No learner performance data found." />
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[900px]">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    {['Learner', 'Batch', 'Date', 'Content', 'Content Type', 'Type', 'Attempts', 'Best Score', 'Last Attempt', 'Status'].map(h => (
                                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {slice.map((r, i) => (
                                    <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                                            <div>{r.learnerName}</div>
                                            <div className="text-xs text-gray-400">{r.email}</div>
                                        </td>
                                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{r.batch}</td>
                                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">{fmtDate(r.enrolledDate)}</td>
                                        <td className="px-4 py-3 text-gray-800 max-w-[180px] truncate" title={r.contentTitle}>{r.contentTitle || '—'}</td>
                                        <td className="px-4 py-3"><TypeBadge type={r.type} /></td>
                                        <td className="px-4 py-3 text-gray-600 text-xs">{fmt(r.contentFormat) || '—'}</td>
                                        <td className="px-4 py-3 text-gray-600 text-center">{r.type === 'assessment' ? r.attempts : '—'}</td>
                                        <td className="px-4 py-3 text-gray-700 font-medium text-center">
                                            {r.type === 'assessment' && r.attempts > 0 ? `${r.bestScore}%` : '—'}
                                        </td>
                                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">{r.type === 'assessment' ? fmtDate(r.lastAttempt) : '—'}</td>
                                        <td className="px-4 py-3"><StatusBadge value={r.status} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination page={page} total={total} pages={pages} onChange={setPage} />
                </>
            )}
        </>
    );
}

function ContentPerformanceTab({ departments, filters, onFiltersChange, onExport }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);

    useEffect(() => {
        setLoading(true); setPage(0);
        reportSvc.getContentPerformance(filters)
            .then(r => { if (r.ok) setRows(r.data?.rows || []); })
            .finally(() => setLoading(false));
    }, [filters.departmentId, filters.type]);

    const filteredRows = rows.filter(r => {
        if (!filters.search?.trim()) return true;
        const q = filters.search.trim().toLowerCase();
       return r.contentTitle?.toLowerCase().split(/\s+/).some(word => word.startsWith(q));
    });

    const { slice, total, pages } = paginate(filteredRows, page);

    return (
        <>
            <FilterBar departments={departments} filters={filters} onChange={onFiltersChange} showType onExport={onExport} />
            {loading ? <Skeleton cols={7} /> : filteredRows.length === 0 ? (
                <Empty icon={BookOpen} message="No content performance data found." />
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[700px]">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    {['Content', 'Content Type', 'Type', 'Enrolled', 'Passed', 'Pass Rate', 'Avg Score'].map(h => (
                                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {slice.map((r, i) => (
                                    <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-4 py-3 font-medium text-gray-900 max-w-[200px] truncate" title={r.contentTitle}>{r.contentTitle || '—'}</td>
                                        <td className="px-4 py-3"><TypeBadge type={r.type} /></td>
                                        <td className="px-4 py-3 text-gray-600 text-xs">{fmt(r.contentFormat) || '—'}</td>
                                        <td className="px-4 py-3 text-gray-700 font-medium">{r.enrolled}</td>
                                        <td className="px-4 py-3 text-gray-600">{r.passed ?? '—'}</td>
                                        <td className="px-4 py-3 text-gray-700">
                                            {r.passRate != null ? (
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full max-w-[60px]">
                                                        <div className="h-full bg-green-500 rounded-full" style={{ width: `${r.passRate}%` }} />
                                                    </div>
                                                    <span>{r.passRate}%</span>
                                                </div>
                                            ) : '—'}
                                        </td>
                                        <td className="px-4 py-3 text-gray-700">{r.avgScore != null ? `${r.avgScore}%` : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination page={page} total={total} pages={pages} onChange={setPage} />
                </>
            )}
        </>
    );
}

// function CertificatesTab({ departments, filters, onFiltersChange, onExport }) {
//     const [rows, setRows] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [page, setPage] = useState(0);

//     useEffect(() => {
//         setLoading(true); setPage(0);
//         reportSvc.getCertificates(filters)
//             .then(r => { if (r.ok) setRows(r.data?.rows || []); })
//             .finally(() => setLoading(false));
//     }, [filters]);

//     const { slice, total, pages } = paginate(rows, page);

//     return (
//         <>
//             <FilterBar departments={departments} filters={filters} onChange={onFiltersChange} showType={false} onExport={onExport} />
//             {loading ? <Skeleton cols={6} /> : rows.length === 0 ? (
//                 <Empty icon={Award} message="No certificates found." />
//             ) : (
//                 <>
//                     <div className="overflow-x-auto">
//                         <table className="w-full text-sm min-w-[650px]">
//                             <thead className="bg-gray-50 border-b border-gray-100">
//                                 <tr>
//                                     {['Learner', 'Batch', 'Content', 'Content Type', 'Completed', 'Score'].map(h => (
//                                         <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
//                                     ))}
//                                 </tr>
//                             </thead>
//                             <tbody className="divide-y divide-gray-50">
//                                 {slice.map((r, i) => (
//                                     <tr key={i} className="hover:bg-gray-50/50 transition-colors">
//                                         <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
//                                             <div>{r.learnerName}</div>
//                                             <div className="text-xs text-gray-400">{r.email}</div>
//                                         </td>
//                                         <td className="px-4 py-3 text-gray-600">{r.batch}</td>
//                                         <td className="px-4 py-3 text-gray-800 max-w-[180px] truncate" title={r.contentTitle}>{r.contentTitle}</td>
//                                         <td className="px-4 py-3"><TypeBadge type={r.type} /></td>
//                                         <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(r.completedDate)}</td>
//                                         <td className="px-4 py-3"><span className="font-semibold text-green-700">{r.score}%</span></td>
//                                     </tr>
//                                 ))}
//                             </tbody>
//                         </table>
//                     </div>
//                     <Pagination page={page} total={total} pages={pages} onChange={setPage} />
//                 </>
//             )}
//         </>
//     );
// }

function LearnerActivityTab({ departments, filters, onFiltersChange, onExport }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);

    useEffect(() => {
        setLoading(true); setPage(0);
        reportSvc.getLearnerActivity(filters)
            .then(r => { if (r.ok) setRows(r.data?.rows || []); })
            .finally(() => setLoading(false));
    }, [filters.departmentId]);

    const filteredRows = rows.filter(r => {
        if (!filters.search?.trim()) return true;
        const q = filters.search.trim().toLowerCase();
       return r.learnerName?.toLowerCase().split(/\s+/).some(word => word.startsWith(q));
    });

    const { slice, total, pages } = paginate(filteredRows, page);

    return (
        <>
            <FilterBar departments={departments} filters={filters} onChange={onFiltersChange} showDepartment={false} showType={false} onExport={onExport} />
            {loading ? <Skeleton cols={5} /> : filteredRows.length === 0 ? (
                <Empty icon={Activity} message="No activity records found." />
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[600px]">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    {['Learner', 'Batch', 'Action', 'Entity', 'Date'].map(h => (
                                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {slice.map((r, i) => (
                                    <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                                            <div>{r.learnerName}</div>
                                            <div className="text-xs text-gray-400">{r.email}</div>
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">{r.batch}</td>
                                        <td className="px-4 py-3 text-gray-800">{fmt(r.action) || '—'}</td>
                                        <td className="px-4 py-3 text-gray-500 text-xs">{fmt(r.entityType) || '—'}</td>
                                        <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(r.activityDate)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination page={page} total={total} pages={pages} onChange={setPage} />
                </>
            )}
        </>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const TABS = [
    { key: 'learner-performance', label: 'Learner Performance', icon: Users },
    { key: 'content-performance', label: 'Content Performance', icon: BookOpen },
    { key: 'course-content',      label: 'Courses',             icon: BarChart2 },
    { key: 'assessment-content',  label: 'Assessments',         icon: ClipboardList },
    // { key: 'certificates',        label: 'Certificates',        icon: Award },
    { key: 'learner-activity',    label: 'Learner Activity',    icon: Activity },
];

export default function ReportsPage() {
    const [activeTab, setActiveTab] = useState('learner-performance');
    const [departments, setDepartments] = useState([]);
    const [filters, setFilters] = useState({ departmentId: null, type: null, status: null, search: '' });

    useEffect(() => {
        reportSvc.getDepartments()
            .then(r => { if (r.ok) setDepartments(r.data?.departments || []); });
    }, []);

    const handleTabChange = (key) => {
        setActiveTab(key);
        setFilters({ departmentId: null, type: null, status: null, search: '' });
    };

    const handleExport = () => reportSvc.exportReport(activeTab, filters);
    const tabProps = { departments, filters, onFiltersChange: setFilters, onExport: handleExport };

    return (
        <Page title="Reports">
            <div className="px-6 py-6">
                <div className="mb-6">
                    <h1 className="text-xl font-bold text-gray-900">Reports</h1>
                    <p className="text-sm text-gray-500 mt-1">Learning insights and analytics for your organisation</p>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="flex border-b border-gray-200 px-5 overflow-x-auto">
                        {TABS.map(t => {
                            const active = activeTab === t.key;
                            return (
                                <button
                                    key={t.key}
                                    onClick={() => handleTabChange(t.key)}
                                    className={`flex items-center gap-2 px-4 py-3.5 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${active
                                        ? 'border-purple-600 text-purple-700'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    <t.icon size={14} />
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>

                    {activeTab === 'learner-performance' && <LearnerPerformanceTab {...tabProps} />}
                    {activeTab === 'content-performance' && <ContentPerformanceTab {...tabProps} />}
                    {activeTab === 'course-content'      && <CourseContentTab />}
                    {activeTab === 'assessment-content'  && <AssessmentContentTab />}
                    {/* {activeTab === 'certificates'        && <CertificatesTab        {...tabProps} />} */}
                    {activeTab === 'learner-activity'    && <LearnerActivityTab     {...tabProps} />}
                </div>
            </div>
        </Page>
    );
}
